/**
 * "Screen orientation" in Settings: Off / Portrait / Landscape.
 *
 * Renders nothing on the web, and nothing if the native plugin doesn't answer —
 * an APK built before OrientationPlugin existed must not show buttons that do
 * nothing. The saved value is read from the phone, not the account: which way
 * you want a screen locked is a fact about that device, not about you.
 */

import { useEffect, useState } from 'react'

import {
  getOrientationLock,
  setOrientationLock,
  type OrientationMode,
} from '../native/orientation'

const OPTIONS: { value: OrientationMode; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
]

export function OrientationLock() {
  // undefined = still asking, null = not available here.
  const [mode, setMode] = useState<OrientationMode | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    void getOrientationLock().then((m) => { if (!cancelled) setMode(m) })
    return () => { cancelled = true }
  }, [])

  if (!mode) return null

  const choose = async (next: OrientationMode) => {
    if (next === mode || busy) return
    const previous = mode
    setBusy(true)
    setError(false)
    // Highlight the choice straight away; the rotation itself follows a beat
    // later, and a button that lags behind the screen reads as broken.
    setMode(next)
    const applied = await setOrientationLock(next)
    if (applied === null) {
      setMode(previous)
      setError(true)
    } else {
      setMode(applied)
    }
    setBusy(false)
  }

  return (
    <>
      <h2>Screen orientation</h2>
      <div className="card">
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              className={mode === o.value ? '' : 'secondary'}
              disabled={busy}
              style={{ marginTop: 0, flex: '1 1 5rem' }}
              onClick={() => void choose(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
        {error && (
          <div className="error" style={{ marginTop: '.5rem' }}>
            Couldn't change the lock — try again.
          </div>
        )}
        <p className="muted" style={{ marginBottom: 0 }}>
          Off follows your phone's own auto-rotate setting. Portrait or Landscape
          keeps Lift that way however the phone is turned — handy when it's
          propped on a bench or lying flat between sets.
        </p>
      </div>
    </>
  )
}
